/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

/**
 * A deploy replaces every hashed chunk, so a tab holding the previous index.html fails its next
 * lazy import. Chunks load through native `import()`, so the failure is the browser's own wording:
 * Chrome "Failed to fetch dynamically imported module", Firefox "error loading dynamically imported
 * module", Safari "Importing a module script failed".
 */
const STALE_CHUNK_MESSAGE = /dynamically imported module|Importing a module script failed/i;
const LAST_RELOAD_KEY = "manatee.staleChunkReload";
const RELOAD_COOLDOWN_MS = 30_000;

export function isStaleChunkError(message: unknown): boolean {
  return typeof message === "string" && STALE_CHUNK_MESSAGE.test(message);
}

function messageOf(event: Event): unknown {
  if (event instanceof ErrorEvent) return event.message;
  const reason: unknown = (event as { reason?: unknown }).reason;
  return reason instanceof Error ? reason.message : reason;
}

function reloadedRecently(now: number): boolean {
  const last = Number(sessionStorage.getItem(LAST_RELOAD_KEY));
  return last > 0 && now - last < RELOAD_COOLDOWN_MS;
}

/**
 * Reloads once when the message is a chunk load failure and answers whether it did. The cooldown
 * lives in sessionStorage because the reload discards in-memory state; without it a chunk that
 * keeps failing would reload forever.
 */
export function reloadIfStaleChunk(message: unknown, reload: () => void = () => window.location.reload()): boolean {
  if (!isStaleChunkError(message)) return false;
  const now = Date.now();
  if (reloadedRecently(now)) return true;
  sessionStorage.setItem(LAST_RELOAD_KEY, String(now));
  reload();
  return true;
}

/**
 * Window-level net for chunk failures outside React's reach. A failed `React.lazy` import never
 * gets here: React hands it to the nearest error boundary, which calls `reloadIfStaleChunk` itself.
 */
export function registerStaleChunkReload(reload: () => void = () => window.location.reload()): () => void {
  const handle = (event: Event) => {
    reloadIfStaleChunk(messageOf(event), reload);
  };
  window.addEventListener("error", handle);
  window.addEventListener("unhandledrejection", handle);
  return () => {
    window.removeEventListener("error", handle);
    window.removeEventListener("unhandledrejection", handle);
  };
}
